import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Plus, Trash2, Edit, Ticket, Check, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDiscountCodes = () => {
  const { adminAuth } = useOutletContext();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    max_uses: '',
    valid_from: '',
    valid_until: '',
    active: true,
    is_wheel_code: false,
  });

  const auth = () => ({ Authorization: `Basic ${adminAuth}` });

  const loadCodes = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/discount-codes`, { headers: auth() });
      if (res.ok) {
        setCodes(await res.json());
      } else {
        toast.error('Failed to load discount codes');
      }
    } catch {
      toast.error('Connection error');
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadCodes(); }, [loadCodes]);

  const resetForm = () => {
    setFormData({
      code: '',
      discount_type: 'percentage',
      discount_value: '',
      max_uses: '',
      valid_from: '',
      valid_until: '',
      active: true,
      is_wheel_code: false,
    });
    setEditingCode(null);
  };

  const openModal = (code = null) => {
    if (code) {
      setEditingCode(code);
      setFormData({
        code: code.code,
        discount_type: code.discount_type,
        discount_value: code.discount_value,
        max_uses: code.max_uses || '',
        valid_from: code.valid_from || '',
        valid_until: code.valid_until || '',
        active: code.active,
        is_wheel_code: code.is_wheel_code || false,
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleSave = async () => {
    if (!formData.code.trim()) {
      toast.error('Please enter a discount code');
      return;
    }
    if (!formData.discount_value) {
      toast.error('Please enter a discount value');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        code: formData.code.toUpperCase(),
        discount_type: formData.discount_type,
        discount_value: parseFloat(formData.discount_value),
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        valid_from: formData.valid_from || null,
        valid_until: formData.valid_until || null,
        active: formData.active,
        is_wheel_code: formData.is_wheel_code,
      };

      let res;
      if (editingCode) {
        res = await fetch(`${API_URL}/api/admin/discount-codes/${editingCode.id}`, {
          method: 'PUT',
          headers: { ...auth(), 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        res = await fetch(`${API_URL}/api/admin/discount-codes`, {
          method: 'POST',
          headers: { ...auth(), 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }

      const data = await res.json();
      if (res.ok) {
        toast.success(editingCode ? 'Discount code updated!' : 'Discount code created!');
        loadCodes();
        closeModal();
      } else {
        toast.error(data.detail || 'Failed to save');
      }
    } catch {
      toast.error('Connection error');
    }
    setSaving(false);
  };

  const handleDelete = async (codeId, codeName) => {
    if (!window.confirm(`Delete discount code "${codeName}"?`)) return;

    try {
      const res = await fetch(`${API_URL}/api/admin/discount-codes/${codeId}`, {
        method: 'DELETE',
        headers: auth(),
      });
      if (res.ok) {
        toast.success('Discount code deleted');
        loadCodes();
      } else {
        toast.error('Failed to delete');
      }
    } catch {
      toast.error('Connection error');
    }
  };

  const toggleActive = async (code) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/discount-codes/${code.id}`, {
        method: 'PUT',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: !code.active }),
      });
      if (res.ok) {
        toast.success(`Code ${code.active ? 'deactivated' : 'activated'}`);
        loadCodes();
      }
    } catch {
      toast.error('Connection error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Discount Codes</h1>
          <p className="text-gray-500">Manage promotional codes for Instant Itinerary</p>
        </div>
        <Button onClick={() => openModal()} className="bg-amber-600 hover:bg-amber-700">
          <Plus className="w-4 h-4 mr-2" /> Create Code
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-amber-600">{codes.length}</div>
            <div className="text-sm text-gray-500">Total Codes</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-600">{codes.filter(c => c.active).length}</div>
            <div className="text-sm text-gray-500">Active</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-600">{codes.reduce((sum, c) => sum + (c.used_count || 0), 0)}</div>
            <div className="text-sm text-gray-500">Total Uses</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-gray-600">{codes.filter(c => !c.active).length}</div>
            <div className="text-sm text-gray-500">Inactive</div>
          </CardContent>
        </Card>
      </div>

      {/* Codes List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Ticket className="w-5 h-5 text-amber-600" /> All Discount Codes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {codes.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Ticket className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No discount codes yet. Create your first one!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Code</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Discount</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Uses</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Validity</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Status</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {codes.map((code) => (
                    <tr key={code.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-amber-600 bg-amber-50 px-2 py-1 rounded">
                            {code.code}
                          </span>
                          {code.is_wheel_code && (
                            <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded" title="Appears on Spin Wheel">
                              🎡
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-semibold">
                          {code.discount_type === 'percentage' 
                            ? `${code.discount_value}% OFF` 
                            : `₹${code.discount_value} OFF`}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-gray-600">
                          {code.used_count || 0}
                          {code.max_uses ? ` / ${code.max_uses}` : ' / ∞'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {code.valid_from || code.valid_until ? (
                          <>
                            {code.valid_from && <div>From: {code.valid_from}</div>}
                            {code.valid_until && <div>Until: {code.valid_until}</div>}
                          </>
                        ) : (
                          <span className="text-green-600">Always valid</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => toggleActive(code)}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                            code.active 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {code.active ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                          {code.active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openModal(code)}
                            className="p-1 text-gray-500 hover:text-amber-600"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(code.id, code.code)}
                            className="p-1 text-gray-500 hover:text-red-600"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>{editingCode ? 'Edit Discount Code' : 'Create Discount Code'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Code *</label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="e.g., WELCOME20, SUMMER50"
                  className="font-mono"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Discount Type</label>
                  <select
                    value={formData.discount_type}
                    onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (₹)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Value *</label>
                  <Input
                    type="number"
                    value={formData.discount_value}
                    onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                    placeholder={formData.discount_type === 'percentage' ? 'e.g., 20' : 'e.g., 50'}
                    min="0"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Max Uses (optional)</label>
                <Input
                  type="number"
                  value={formData.max_uses}
                  onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                  placeholder="Leave empty for unlimited"
                  min="1"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Valid From</label>
                  <Input
                    type="date"
                    value={formData.valid_from}
                    onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Valid Until</label>
                  <Input
                    type="date"
                    value={formData.valid_until}
                    onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="active"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="rounded"
                />
                <label htmlFor="active" className="text-sm">Active (can be used by customers)</label>
              </div>

              <div className="flex items-center gap-2 bg-amber-50 p-3 rounded-lg border border-amber-200">
                <input
                  type="checkbox"
                  id="is_wheel_code"
                  checked={formData.is_wheel_code}
                  onChange={(e) => setFormData({ ...formData, is_wheel_code: e.target.checked })}
                  className="rounded"
                />
                <label htmlFor="is_wheel_code" className="text-sm">
                  <span className="font-medium text-amber-700">🎡 Show on Spin Wheel</span>
                  <span className="block text-xs text-amber-600">This code will appear as a prize on the lucky wheel (percentage discounts only)</span>
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={closeModal} className="flex-1">
                  Cancel
                </Button>
                <Button 
                  onClick={handleSave} 
                  disabled={saving}
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {editingCode ? 'Update' : 'Create'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminDiscountCodes;
